import React, { useState, useRef, useCallback } from "react";
import { Info } from "@phosphor-icons/react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

/* A 529 balance can be entered in two places — anchoring one in Accounts, or the
   "balance before this distribution" field on a distribution. Both are readings
   taken BEFORE that day's money moved, and the app applies same-day activity
   itself, so an owner who nets a withdrawal out by hand double-counts it. One
   copy of the warning, shown in both places. */
export const BALANCE_ENTRY_TIP =
  "Enter the balance exactly as your statement shows it. Do not subtract a distribution yourself — " +
  "the app subtracts every distribution dated on or after this date, and adds every contribution.";

/* ═══ ENTER MOVES TO THE NEXT FIELD — ONE RULE, ONE PLACE (ZEN-55) ═══════════

   Every dialog in the app gets this by having a DialogContent, not by each form
   opting in, and a field added tomorrow inherits it by existing. Editing twelve
   screens to add twelve key handlers is the failure this replaces.

   WHAT ENTER MUST STILL DO ELSEWHERE, and how each case stays out of the way:

     · a textarea needs Enter for a new line;
     · a button or a link needs Enter to press it;
     · a picker showing a highlighted suggestion — the school search, the
       currency search — needs Enter to choose it;
     · a field whose whole job is "type a name, press Enter to add it" needs
       Enter to add it.

   The last two already call preventDefault(), so the rule is simply: a keypress
   somebody has ALREADY given a meaning is left alone. That is what makes this
   safe to switch on everywhere at once — every existing Enter handler in the
   app was checked, and each one either prevents the default or genuinely wants
   the advance. Anything discovered later opts out by sitting inside
   data-enter="keep".

   The date field is the one component that both handles Enter and wants the
   advance: it commits the date the owner typed, then marks itself
   data-enter="advance" so this rule runs afterwards anyway.

   ON THE LAST FIELD, Enter moves to the form's primary action and stops there —
   it does not press it. Focus lands on Save with a visible ring and a second
   Enter saves. Pressing it outright was rejected deliberately: a saved expense
   in this app is a tax record, and no stray keystroke should be able to file
   one. "Advance, then land on Save" is also the same answer in every dialog,
   which is what the ticket asked for by "consistently". */

// data-enter-field lets a control that is drawn as a BUTTON — the currency
// picker is one — take its place in the field order. Without it the walk stepped
// straight over the currency and into the amount. (Adversarial review, round 3.)
const FIELD_SELECTOR = "input, select, textarea, [data-enter-field]";

/* Enter has a native meaning inside these — pressing them, not leaving them.
   Tick boxes and radio buttons are deliberately NOT on this list: Enter does
   nothing at all in one, so advancing costs nothing and closes a hole. The
   payment-account form ends on a tick box, and without this Enter died there
   instead of reaching Save like every other form in the app. */
const SELF_HANDLING_INPUT_TYPES = new Set([
  "submit", "button", "reset", "file", "image",
]);

/* Content a popover put on screen is not part of the form's field order. Radix
   wraps every portaled popover in this element, so one test covers the currency
   search, the calendar, and anything else that opens over the form. */
const FLOATING = "[data-radix-popper-content-wrapper]";

/* THE OWNER HAS TO BE ABLE TO SEE WHERE THE KEYBOARD WENT.
   Some controls in this app are deliberately shrunk to a screen-reader-sized
   speck and drawn as a styled label around them — the "will you claim the tax
   credit?" answers on a beneficiary are three radio buttons one pixel square,
   with no focus styling on the label at all. Advancing onto one would take the
   keyboard somewhere invisible: Enter, and the cursor is nowhere, with nothing
   on screen to say where it went. So anything too small to show a focus ring is
   passed over. Tab still reaches them, which is what they were built for.

   DEFENCE IN DEPTH, AND SAID PLAINLY: there is no end-to-end test behind this
   line, because today the case cannot be reached. Those three radio buttons are
   the only specks in any form, and the field immediately before them is a
   textarea — where Enter is a new line and this rule never runs. A browser test
   WAS written, and it passed just as happily with this check removed, so it was
   deleted rather than kept as a false assurance. The check stays because that
   form is one edited field away from making the case live, and because focus
   landing nowhere is invisible to whoever changes it next. */
const SMALLEST_VISIBLE = 4;   // pixels; a real field is ~36 tall

function isReachableField(el) {
  if (el.disabled || el.readOnly) return false;
  if ((el.type || "").toLowerCase() === "hidden") return false;
  if (el.tabIndex < 0) return false;
  const box = el.getBoundingClientRect();
  if (box.width < SMALLEST_VISIBLE || box.height < SMALLEST_VISIBLE) return false;
  if (el.ownerDocument.defaultView.getComputedStyle(el).visibility === "hidden") return false;
  if (el.closest('[data-enter="keep"]')) return false;
  if (el.closest(FLOATING)) return false;
  return true;
}

/* Does THIS keypress mean "I have finished this field"? */
function meansNextField(e) {
  if (e.key !== "Enter") return false;
  // An input method editor is confirming a candidate word, not finishing the
  // field. Without this, Enter in Japanese or Chinese jumps a field mid-word.
  if (e.nativeEvent?.isComposing || e.keyCode === 229) return false;
  if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return false;

  const t = e.target;
  if (!t || !t.tagName || typeof t.closest !== "function") return false;
  if (t.closest('[data-enter="keep"]')) return false;

  // Somebody has already given this keypress a meaning — unless they explicitly
  // asked for the advance as well (the date field).
  if (e.defaultPrevented && t.dataset?.enter !== "advance") return false;

  const tag = t.tagName.toLowerCase();
  // A textarea keeps Enter for a new line — unless it is a one-paragraph box
  // that asks for the advance (the shared Notes box, ZEN-772).
  const oneParagraph = tag === "textarea" && t.dataset?.enter === "advance";
  if (tag !== "input" && tag !== "select" && !oneParagraph) return false;
  if (t.isContentEditable) return false;
  if (tag === "input" && SELF_HANDLING_INPUT_TYPES.has((t.type || "").toLowerCase())) return false;

  // A SEARCH BOX WITH ITS LIST OPEN takes Enter to choose. A search box with
  // nothing showing is an ordinary field and must advance like one — marking the
  // school search as a combo box unconditionally made Enter dead in it even when
  // no suggestion had appeared. Judged by whether it is CURRENTLY offering
  // choices, which is what aria-expanded is for. (Adversarial review, round 3.)
  const role = t.getAttribute("role");
  const offeringChoices = t.getAttribute("aria-expanded") === "true";
  if ((role === "combobox" || role === "searchbox") && offeringChoices) return false;
  if (t.getAttribute("aria-autocomplete") && offeringChoices) return false;
  if (t.closest("[cmdk-root]") || t.closest(FLOATING)) return false;

  return true;
}

function putCaretAtEnd(el) {
  // Throws on the input types that have no text selection (number, email…),
  // where focus alone is the whole job.
  try { const n = el.value.length; el.setSelectionRange(n, n); } catch { /* not a text field */ }
}

/* The button Enter lands on when the fields run out.

   ONLY A BUTTON THAT HAS SAID IT IS SAFE TO LAND ON. The first version took the
   last live button in the dialog's footer, on the reasoning that every dialog
   here ends in Save. That is wrong in the dialogs where it matters most: the two
   delete confirmations end in a red **Delete**, so Enter would have parked the
   keyboard on it and a second Enter would have deleted the thing. Landing on a
   destructive action is worse than not landing anywhere, so this now REQUIRES an
   opt-in and gives up when there is none. (Adversarial review, round 1.)

   The opt-in is marked by the shared Btn below, which every save/confirm in the
   app is built from, and deliberately NOT by its danger variant or by the raw
   red buttons in the delete dialogs — so a destructive action can only become an
   Enter target if somebody marks it by hand. */
function primaryActionIn(container, selector) {
  if (selector) {
    const chosen = container.querySelector(selector);
    if (chosen && !chosen.disabled) return chosen;
  }
  const scope = container.querySelector("[data-dialog-footer]") || container;
  const live = Array.from(scope.querySelectorAll("button[data-enter-primary]")).filter(
    (b) => !b.disabled && b.tabIndex >= 0 && !b.closest(FLOATING) && !b.closest("[data-dialog-close]"),
  );
  return live[live.length - 1] || null;
}

/**
 * The shared handler. Attach it to whatever element encloses the fields.
 *
 * Only DialogContent does today, and that is enough to cover every form in the
 * app: the sign-in and sign-up screens are the only field groups outside a
 * dialog, and they are real <form> elements where Enter already submits — which
 * is what everyone expects a sign-in box to do, and must not change.
 *
 * A page-level form that ever needs this passes a selector for its primary
 * action, because it has no dialog footer to read.
 */
export function useEnterAdvances(primarySelector) {
  // The button this rule last moved the keyboard onto. Held Enter would
  // otherwise PRESS it — see the guard at the top of the handler.
  const landedOn = useRef(null);
  return useCallback((e) => {
    // HOLDING ENTER MUST NOT PRESS WHAT IT JUST REACHED.
    //
    // The whole safety of "land on Save, do not press it" rests on one keypress
    // doing one thing. It does not: hold the key and the keyboard repeats, the
    // first press moves focus to Save, and every repeat after it arrives AT
    // Save — where Enter on a button is a click. Holding Enter on the last
    // field would have filed the expense. A deliberate second press still
    // saves, which is the intended way through; only the repeats of one
    // uninterrupted press are refused. (Adversarial review, round 3.)
    if (e.key === "Enter" && e.repeat && e.target === landedOn.current) {
      e.preventDefault();
      return;
    }
    if (e.key === "Enter" && !e.repeat) landedOn.current = null;
    if (!meansNextField(e)) return;
    // A PANEL INSIDE A FORM IS ITS OWN FORM. Adding a payment account opens a
    // little form inside the expense; walking the whole dialog stepped straight
    // out of it, past its own Save, and into the expense behind. A subtree that
    // marks itself a scope is walked on its own. (Adversarial review, round 3.)
    const container = e.target.closest?.("[data-enter-scope]") || e.currentTarget;
    const current = e.target;
    const fields = Array.from(container.querySelectorAll(FIELD_SELECTOR)).filter(isReachableField);
    // DOM order, asked of the document rather than of an index — so a field the
    // filter dropped (a read-only one, say) still advances to what follows it.
    const next = fields.find(
      (el) => el !== current &&
        (current.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING),
    );
    const target = next || primaryActionIn(container, primarySelector);
    if (!target) return;
    // MOVE FIRST, THEN CLAIM THE KEYPRESS. If focus refuses to land — a control
    // that turned out not to be focusable after all — Enter is handed back
    // untouched rather than being swallowed for nothing.
    target.focus();
    if (target.ownerDocument.activeElement !== target) return;
    e.preventDefault();
    if (next) putCaretAtEnd(target);
    // Remember a BUTTON we landed on, so a held key cannot press it.
    landedOn.current = next ? null : target;
  }, [primarySelector]);
}

/* ---- Shared info-tooltip — portaled via Radix Popover to escape stacking contexts ---- */
export function InfoTip({ text, children, testid, side = "bottom", size = 14, width = "w-72" }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }, []);
  const cancelClose = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        {/* tabIndex={-1}: Tab moves between fields the owner has to fill, never
            onto help. Info stays reachable by pointer (and by screen readers,
            which navigate by role rather than tab order). Owner ruling, 2026-08-09. */}
        <button type="button" tabIndex={-1}
          onPointerEnter={(e) => { if (e.pointerType !== "touch") { cancelClose(); setOpen(true); } }}
          onPointerLeave={(e) => { if (e.pointerType !== "touch") scheduleClose(); }}
          className="cursor-help text-[#034AA2]/60 transition-colors hover:text-[#034AA2]" aria-label="Info"
          data-testid={testid}>
          <Info size={size} weight="bold" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align="center"
          sideOffset={6}
          collisionPadding={12}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          // A bubble is only read, so opening or closing it never moves the
          // keyboard out of the field being typed in. (ZEN-840)
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          data-testid={testid ? `${testid}-panel` : "infotip-panel"}
          className={`z-[9999] ${width} rounded-lg border border-[#C2C9D1] bg-white p-3 text-[11px] font-normal normal-case leading-relaxed text-[#374151] shadow-lg animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[side=left]:slide-in-from-right-2`}
        >
          {children || text}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-3 @md:flex-row @md:items-center @md:justify-between">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight @md:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 whitespace-pre-line text-sm text-[#6b7280]">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`rounded-xl border border-[#C2C9D1] bg-white p-5 ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function StatCard({ label, value, sub, tip, infoTip, icon: Icon, testid, amber }) {
  return (
    <Card className={`fade-in ${amber ? "!border-[#D97706]/30 !bg-[#FCF5EE]" : ""}`} data-testid={testid}>
      <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6b7280]">
        {Icon && <Icon size={14} weight="bold" />}
        {label}
        {infoTip && <InfoTip text={infoTip} testid={testid ? `${testid}-info` : undefined} />}
      </span>
      <div className="mt-2 text-2xl font-extrabold tnum text-[#1f2937]" data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
      {sub && <div className="mt-1 text-xs font-medium text-[#6b7280]">{sub}</div>}
      {tip && <div className="mt-1.5 text-[11px] italic text-[#9CA3AF]">{tip}</div>}
    </Card>
  );
}

// iconClassName lets a caller signal an outcome rather than an absence — a
// waiting room that has just been emptied is good news and should read green,
// not the neutral blue used for "there is nothing here yet". (ZEN-113)
export function EmptyState({ icon: Icon, title, subtitle, action, subtitleClassName, iconClassName }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#C2C9D1] bg-white py-16 text-center">
      {Icon && <Icon size={42} weight="duotone" className={iconClassName || "mb-3 text-[#034AA2]"} />}
      <div className="text-base font-bold">{title}</div>
      {subtitle && <div className={subtitleClassName || "mt-1 max-w-lg text-sm text-[#6b7280]"}>{subtitle}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Btn({ children, variant = "primary", className = "", enterPrimary = false, ...rest }) {
  const styles = {
    primary: "bg-[#034AA2] text-white hover:bg-[#023a80]",
    green: "bg-[#077A39] text-white hover:bg-[#055f2c]",
    ghost: "border border-[#C2C9D1] bg-white text-[#1f2937] hover:bg-[#E9E9EB]",
    danger: "border border-[#F01919] bg-white text-[#F01919] hover:bg-[#F01919]/10",
    accent: "bg-[#F5D85B] text-[#0A0A0A] hover:bg-[#e9c945]",
  };
  // enterPrimary marks THIS button as the form's primary action — the one Enter
  // may land on once the fields run out (ZEN-55). It has to be passed by hand,
  // one button at a time.
  //
  // IT WAS INFERRED TWICE AND WAS DANGEROUS BOTH TIMES. First "the last button
  // in the footer", which in a delete confirmation is Delete. Then "any button
  // that is not the danger variant", which in the delete-a-beneficiary dialog
  // marked "Archive instead" — a green button that ends that beneficiary's
  // login — and left the raw Cancel and Delete beside it unmarked, so Enter
  // landed on the archive. Both were caught by review rather than by use.
  //
  // So there is no rule to guess with any more. A button is a landing place only
  // if somebody decided it is, and a form nobody has marked simply does not get
  // the behaviour — which is the safe way to be wrong. (Adversarial review,
  // rounds 1 and 2.)
  return (
    <button
      {...(enterPrimary ? { "data-enter-primary": "" } : {})}
      className={`inline-flex h-9 shrink-0 whitespace-nowrap items-center justify-center gap-1.5 rounded-md px-3.5 text-sm font-semibold transition-colors disabled:opacity-60 ${styles[variant]} ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function Disclaimer({ text }) {
  return (
    <div className="mt-6 rounded-lg border border-[#C2C9D1] bg-white px-4 py-3 text-xs text-[#6b7280]">
      <span className="font-bold text-[#7D3306]">⚠ Disclaimer:</span> {text}
    </div>
  );
}
